/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { getB2CCartServiceAsync } from '../../../dataActions/DataActionExtension.g';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>, any> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;
    private signInEvent = new Event('signInEvent', { bubbles: true, cancelable: true, composed: false });
    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
        this.menuNode = React.createRef();
        this.state = {
            cartCount: 0
        };
    }

    public componentDidMount(): void {
        if (this.props.config.useStickyHeader) {
            // Set header update methods to trigger on scroll
            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', () => {
                    this._configureStickyHeader();
                });
            }
            this._configureStickyHeader();
        }
        if (this.props.data.accountInformation.result && !this.props.data.accountInformation.result.IsB2b) {
            void this.getB2cCartCount();
        }
        window.addEventListener('addEvent', event => {
            this.setState({ cartCount: this.state.cartCount + 1 });
        });
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            Divider,
            MobileMenuContainer,
            MobileMenuBodyContainer,
            MobileMenuLinksContainer
        } = this.props;

        const isAuth = this.props.data.accountInformation.result;
        const isB2B = this.props.data.accountInformation.result && this.props.data.accountInformation.result.IsB2b;
        const cartLink = getUrlSync('cart', this.props.context.actionContext);
        console.log('Cart Link Get Url Sync', cartLink);

        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        {/*<div className='desktop-navicon-container' ref={this.menuNode}>*/}
                        {/*    {this._renderReactFragment(this.props.menuBar)}*/}
                        {/*</div>*/}
                        <div className='mobile-navicon-container'>{this.props.navIcon}</div>
                        {this._renderReactFragment(this.props.search)}
                        {this._renderDesktopLogo()}
                        {this._renderMobileLogo()}

                        {this.props.preferredStore}
                        {this._renderAccountBlock(this.props)}
                        <Node {...Divider} />
                        {/*{this.props.cartIcon}*/}
                        {this.props.wishListIconDesktop}
                        {isAuth && !isB2B && (
                            <a href={cartLink}>
                                <span className='header-my-order'>
                                    {/*<span className='header-my-order-label'>My Orders</span>*/}
                                    ({this.state.cartCount})
                                </span>
                            </a>
                        )}
                        {isAuth && isB2B && (
                            <span className='header-my-order'>
                                {this.props.cartIcon}
                                {/*<span className='header-my-order-label'>My Orders</span>*/}
                            </span>
                        )}
                        <div className='desktop-navicon-container' ref={this.menuNode}>
                            {this._renderReactFragment(this.props.menuBar)}
                        </div>
                        {this._renderReactFragment(this.props.siteOptions)}
                    </Node>
                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            {this.props.MobileMenuHeader}
                            {this._renderReactFragment(this.props.menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                {this.props.accountLinks ? this.props.accountLinks.map(link => link) : false}
                                {this.props.siteOptions}
                                {this.props.wishListIconMobile}
                                {this.props.signInLink}
                                {this.props.signOutLink}
                                {isAuth && !isB2B && (
                                    <a href={cartLink}>
                                        <span className='header-my-order'>({this.state.cartCount})</span>
                                    </a>
                                )}
                                {isAuth && isB2B && (
                                    <a href={cartLink}>
                                        <span className='header-my-order'>{this.state.cartCount}</span>
                                    </a>
                                )}
                            </Node>
                        </Node>
                    </Node>
                </Node>
            </Module>
        );
    }

    /**
     * Function to update header.
     */
    private readonly _configureStickyHeader = (): void => {
        // Get heights of cookie and promotion banners
        const defaultValue = 0;
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

        // Triggers opacity change of header
        const headerElement = document.querySelector('header .default-container');
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                headerElement.classList.add('lock-opaque');
                headerElement.classList.add('fixed');
            } else {
                headerElement.classList.remove('lock-opaque');
                headerElement.classList.remove('fixed');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

        if (stickyHeader && headerLogo) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private readonly getB2cCartCount = async () => {
        const cartResult: any = await getB2CCartServiceAsync({ callerContext: this.props.context.actionContext, queryResultSettings: {} });
        if (cartResult.B2CCart && cartResult.B2CCart.length > 0) {
            this.setState({ cartCount: cartResult.B2CCart.length });
        }
    };

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        const newImage: any = { ...config.mobileLogoImage };
        newImage.src = config.logoSvgUrl;
        return (
            <Logo
                {...{
                    link: config.logoLink,
                    image: newImage ?? {},
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName
                }}
            />
        );
    }
    private _renderDesktopLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        const newImage = { ...config.logoImage };
        newImage.src = config.logoSvgUrl;
        return (
            <Logo
                {...{
                    link: config.logoLink,
                    image: newImage ?? {},
                    className: 'ms-header__logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName
                }}
            />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signOutLink,
            signInLink,
            accountLinks
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>
                            {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                            {signOutLink}
                        </Node>
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        <span onClick={this.signInClicked}>{signInLink}</span>
                    </Node>
                );
            }
        }
        return null;
    };
    private readonly signInClicked = (event: any) => {
        console.log('signInClicked');
        if (window['bootstrapMetrixModule']) {
            event.preventDefault();
        }
        window.dispatchEvent(this.signInEvent);
    };
    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                          return <>{slot}</>;
                      })
                    : null}
            </>
        );
    };
}

export default HeaderView;
